import React, { createContext, useContext } from 'react';
import { View } from 'app-studio';
import { Vertical } from 'app-studio';
import { Horizontal } from 'app-studio';
import { Text } from 'app-studio';
import { ChevronIcon } from '../../Icon/Icon';
import {
  SidebarProps,
  SidebarHeaderProps,
  SidebarContentProps,
  SidebarFooterProps,
} from './Sidebar.props';
import {
  SidebarSizes,
  SidebarVariants,
  SidebarPositions,
  SidebarElevations,
  SidebarTransitions,
} from './Sidebar.style';
import { SidebarContextType } from './Sidebar.type';
import { Badge } from '../../Badge/Badge';
// Initializes the React context for the Sidebar component, providing a way to share the sidebar's state and functions throughout the component tree with default values.
const SidebarContext = createContext<SidebarContextType>({
  isExpanded: true,
  toggleExpanded: () => {},
  expand: () => {},
  collapse: () => {},
  position: 'left',
  size: 'md',
  variant: 'default',
});
// Custom hook that allows components to easily consume the `SidebarContext` and access its shared values like `isExpanded` and `toggleExpanded`.
export const useSidebarContext = () => useContext(SidebarContext);
// Provides the `SidebarContext` value to its children, making the sidebar's state and actions available to all descendant components within its scope.
export const SidebarProvider: React.FC<{
  value: SidebarContextType;
  children: React.ReactNode;
}> = ({ value, children }) => {
  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};
// Renders the header section of the Sidebar, optionally including a toggle button to expand or collapse the sidebar based on its current state.
export const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  children,
  showToggleButton = true,
  views,
  ...props
}) => {
  // Retrieves the current expansion state, toggle function, and position from the `SidebarContext` to control the header's appearance and behavior.
  const { isExpanded, toggleExpanded, position } = useSidebarContext();
  return (
    <Horizontal
      width="100%"
      padding="16px"
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor="color-gray-200"
      alignItems="center"
      justifyContent="space-between"
      {...views?.header}
      {...props}
    >
      {isExpanded ? (
        <Horizontal
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          {children}
          {showToggleButton && (
            <View
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="32px"
              height="32px"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="color-gray-200"
              borderRadius="4px"
              backgroundColor="transparent"
              cursor="pointer"
              _hover={{ backgroundColor: 'color-gray-100' }}
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              {...views?.toggleButton}
            >
              <ChevronIcon
                orientation={position === 'left' ? 'left' : 'right'}
                widthHeight={16}
                {...views?.toggleButtonIcon}
              />
            </View>
          )}
        </Horizontal>
      ) : (
        <View
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {showToggleButton && (
            <View
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="32px"
              height="32px"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="color-gray-200"
              borderRadius="4px"
              backgroundColor="transparent"
              cursor="pointer"
              _hover={{ backgroundColor: 'color-gray-100' }}
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              {...views?.toggleButton}
            >
              <ChevronIcon
                orientation={position === 'left' ? 'right' : 'left'}
                widthHeight={16}
                {...views?.toggleButtonIcon}
              />
            </View>
          )}
        </View>
      )}
    </Horizontal>
  );
};
export const SidebarContent: React.FC<SidebarContentProps> = ({
  children,
  views,
  ...props
}) => {
  const { isExpanded } = useSidebarContext();
  return (
    <View
      flex="1"
      width="100%"
      overflowY="auto"
      overflowX="hidden"
      padding={isExpanded ? '16px' : '8px'}
      {...views?.content}
      {...props}
    >
      {children}
    </View>
  );
};
export const SidebarFooter: React.FC<SidebarFooterProps> = ({
  children,
  views,
  ...props
}) => {
  const { isExpanded } = useSidebarContext();
  return (
    <View
      width="100%"
      padding="16px"
      borderTopWidth="1px"
      borderTopStyle="solid"
      borderTopColor="color-gray-200"
      {...views?.footer}
      {...props}
    >
      {isExpanded ? children : null}
    </View>
  );
};
export const SidebarView: React.FC<SidebarProps> = ({
  children,
  position = 'left',
  size = 'md',
  variant = 'default',
  fixed = false,
  hasBackdrop = true,
  expandedWidth,
  collapsedWidth,
  breakpoint,
  breakpointBehavior = 'overlay',
  elevation = 'none',
  transitionPreset = 'normal',
  ariaLabel = 'Sidebar navigation',
  showToggleButton,
  toggleExpanded,
  expand,
  isExpanded,
  isMobile,
  collapse,
  views,
  themeMode: elementMode,
  ...props
}) => {
  const sizeConfig = SidebarSizes[size];
  const width = isExpanded
    ? expandedWidth || sizeConfig.expandedWidth
    : collapsedWidth || sizeConfig.collapsedWidth;
  const isVisible = !isMobile || (isMobile && breakpointBehavior !== 'hide');
  const position_type =
    fixed || (isMobile && breakpointBehavior === 'overlay')
      ? 'fixed'
      : 'relative';
  const showBackdrop =
    hasBackdrop && isMobile && isExpanded && breakpointBehavior === 'overlay';
  return (
    <>
      {}
      {showBackdrop && (
        <View
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          backgroundColor="color-blackAlpha-500"
          zIndex={998}
          onClick={collapse}
          {...views?.backdrop}
        />
      )}
      {}
      {isVisible && (
        <Vertical
          position={position_type}
          top={0}
          height="100vh"
          width={width}
          zIndex={999}
          transition={SidebarTransitions[transitionPreset]}
          transform={
            isMobile && breakpointBehavior === 'overlay' && !isExpanded
              ? `translateX(${position === 'left' ? '-100%' : '100%'})`
              : 'translateX(0)'
          }
          role="navigation"
          aria-label={ariaLabel}
          {...SidebarVariants[variant]}
          {...SidebarPositions[position]}
          {...SidebarElevations[elevation]}
          {...views?.container}
          {...props}
        >
          {React.Children.map(children, (child) => {
            return child;
          })}
        </Vertical>
      )}
    </>
  );
};
interface SideBarNavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  ariaLabel?: string;
  badge?: number;
}
export const SideBarNavItem = ({
  icon,
  label,
  isActive = false,
  isDisabled = false,
  badge,
  views,
  ariaLabel,
  ...props
}: SideBarNavItemProps & any) => {
  const { isExpanded } = useSidebarContext();
  return (
    <Horizontal
      alignItems="center"
      justifyContent={!isExpanded ? 'center' : undefined}
      gap={12}
      padding="8px 12px"
      borderRadius="4px"
      backgroundColor={isActive ? 'color-blue-50' : 'transparent'}
      color={
        isDisabled
          ? 'color-gray-400'
          : isActive
          ? 'color-blue-600'
          : 'color-gray-700'
      }
      fontWeight={isActive ? 'bold' : 'normal'}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.6 : 1}
      aria-label={ariaLabel}
      aria-disabled={isDisabled || undefined}
      on={{
        hover: isDisabled
          ? {}
          : {
              backgroundColor: isActive ? 'color-blue-50' : 'color-gray-100',
            },
      }}
      {...props}
    >
      {icon}
      {isExpanded && <Text>{label}</Text>}
      {isExpanded && badge && <Badge {...views?.navItemBagde}>{badge}</Badge>}
    </Horizontal>
  );
};
